import React, { useEffect, useState } from "react";
import api from "../../../api";
import RichTextEditor from "./RichTextEditor";

interface Recipe {
    id: number;
    name: string;
}

interface RecipeIngredientSection {
    id: number;
    recipe_id: number;
    component_name: string;
    ingredients: string;
    display_order: number;
}

interface RecipeIngredientSectionModalProps {
    recipe: Recipe;
    section: RecipeIngredientSection | null;
    onClose: () => void;
    onSaved: () => void;
}

const RecipeIngredientSectionModal = ({
                                          recipe,
                                          section,
                                          onClose,
                                          onSaved,
                                      }: RecipeIngredientSectionModalProps) => {
    const [componentName, setComponentName] =
        useState("");

    const [ingredients, setIngredients] =
        useState("");

    const [loading, setLoading] = useState(false);

    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");

    const isEditing = !!section;

    useEffect(() => {
        if (section) {
            setComponentName(
                section.component_name || ""
            );

            setIngredients(
                section.ingredients || ""
            );
        } else {
            setComponentName("");
            setIngredients("");
        }

        setError("");
        setSuccess("");
    }, [section]);

    const handleSave = async () => {
        setError("");
        setSuccess("");

        if (!componentName.trim()) {
            setError(
                "Please enter a component name."
            );
            return;
        }

        if (!ingredients.trim()) {
            setError(
                "Please enter the ingredients."
            );
            return;
        }

        setLoading(true);

        try {
            const payload = {
                recipe_id: recipe.id,
                component_name:
                    componentName.trim(),
                ingredients,
                ...(isEditing
                    ? {}
                    : {
                        display_order: 0,
                    }),
            };

            if (isEditing) {
                await api.put(
                    `/admin/recipe-ingredient-sections/${section.id}`,
                    payload
                );

                setSuccess(
                    "Ingredient section updated successfully."
                );
            } else {
                await api.post(
                    "/admin/recipe-ingredient-sections",
                    payload
                );

                setSuccess(
                    "Ingredient section added successfully."
                );
            }

            await onSaved();
        } catch (err: any) {
            console.error(
                "Failed to save ingredient section:",
                err
            );

            if (
                err.response?.data?.errors
            ) {
                const validationErrors =
                    Object.values(
                        err.response.data.errors
                    ).flat();

                setError(
                    validationErrors.length
                        ? String(
                            validationErrors[0]
                        )
                        : "Please check the form and try again."
                );
            } else {
                setError(
                    err.response?.data?.message ||
                    "Failed to save ingredient section."
                );
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div
            className="admin-modal-overlay"
            onClick={onClose}
        >
            <div
                className="admin-modal admin-recipe-ingredient-modal"
                onClick={(event) =>
                    event.stopPropagation()
                }
            >
                <div className="admin-modal-header">
                    <h2>
                        {isEditing
                            ? "Edit Ingredient Section"
                            : "Add Ingredient Section"}
                    </h2>

                    <button
                        type="button"
                        className="admin-modal-close"
                        onClick={onClose}
                        disabled={loading}
                    >
                        ×
                    </button>
                </div>

                <div className="admin-modal-content">
                    {error && (
                        <div className="form-error">
                            {error}
                        </div>
                    )}

                    {success && (
                        <div className="form-success">
                            {success}
                        </div>
                    )}

                    <div className="form-group">
                        <label>
                            Recipe
                        </label>

                        <input
                            type="text"
                            value={recipe.name}
                            disabled
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="ingredient-component-name">
                            Component Name
                        </label>

                        <input
                            id="ingredient-component-name"
                            type="text"
                            value={componentName}
                            onChange={(event) =>
                                setComponentName(
                                    event.target.value
                                )
                            }
                            placeholder="e.g. Dressing"
                            disabled={loading}
                        />
                    </div>

                    <div className="form-group">
                        <label>
                            Ingredients
                        </label>

                        <RichTextEditor
                            value={ingredients}
                            onChange={setIngredients}
                            placeholder="Enter the ingredients..."
                        />
                    </div>
                </div>

                <div className="admin-modal-actions">
                    <button
                        type="button"
                        className="admin-table-button"
                        onClick={onClose}
                        disabled={loading}
                    >
                        Cancel
                    </button>

                    <button
                        type="button"
                        className="admin-primary-button"
                        onClick={handleSave}
                        disabled={loading}
                    >
                        {loading
                            ? "Saving..."
                            : isEditing
                                ? "Save Changes"
                                : "Add Ingredient Section"}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default RecipeIngredientSectionModal;
